import { useEffect, useState } from "preact/hooks";
import {
  getOrCreateProfile,
  listCharacters,
  listMyStories,
  listPublishedStories,
  updateDisplayName,
  type CharacterRow,
  type StoryRow,
} from "@/lib/api";
import { isMockEnabled, isSupabaseConfigured } from "@/lib/supabase";
import { BUILTIN_STORY_ID, BUILTIN_STORY_TITLE } from "@/story/builtin";
import { navigate } from "@/router";
import { ButtonGroup, ToastBanner } from "./ui";

export function Hub() {
  const [published, setPublished] = useState<StoryRow[]>([]);
  const [mine, setMine] = useState<StoryRow[]>([]);
  const [characters, setCharacters] = useState<CharacterRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [displayName, setDisplayName] = useState("");
  const [banner, setBanner] = useState<{
    kind: "error" | "ok";
    message: string;
  } | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      if (!isSupabaseConfigured()) {
        setLoading(false);
        return;
      }
      const profileResult = await getOrCreateProfile();
      if (profileResult.profile) {
        setDisplayName(profileResult.profile.display_name ?? "Anonymous Dev");
      }
      const [pub, my, chars] = await Promise.all([
        listPublishedStories(),
        listMyStories(),
        listCharacters(true),
      ]);
      if (cancelled) return;
      setPublished(pub);
      setMine(my);
      setCharacters(chars);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const saveName = async (): Promise<void> => {
    const err = await updateDisplayName(displayName.trim() || "Anonymous Dev");
    setBanner(
      err
        ? { kind: "error", message: err }
        : { kind: "ok", message: "Display name saved" }
    );
  };

  if (!isSupabaseConfigured()) {
    return <OfflineHub />;
  }

  return (
    <div className="hub">
      <header className="hub-header">
        <h1 className="hub-logo">DevAdventure2K</h1>
        <div className="hub-actions">
          <label className="mini-name">
            <span>Your name</span>
            <input
              className="editor-input"
              value={displayName}
              onInput={(e) => setDisplayName(e.currentTarget.value)}
              onBlur={() => void saveName()}
            />
          </label>
          <button
            className="choice-button"
            onClick={() => navigate("/edit/story")}
          >
            + New story
          </button>
          <button
            className="choice-button"
            onClick={() => navigate("/edit/character")}
          >
            + New character
          </button>
        </div>
      </header>

      <ToastBanner
        kind={banner?.kind ?? "ok"}
        message={banner?.message ?? null}
      />

      {isMockEnabled() ? (
        <ToastBanner
          kind="warn"
          message="Local mock Supabase — data stays in this browser."
        />
      ) : null}

      {loading ? <p className="muted">Loading the hub…</p> : null}

      <section className="hub-section">
        <h2>Play a story</h2>
        <div className="story-grid">
          <StoryCard
            key={BUILTIN_STORY_ID}
            storyLite={{
              title: BUILTIN_STORY_TITLE,
              subtitle: "The bundled dev-roguelike. Works offline.",
            }}
            plays={0}
            author="DevAdventure2K"
            onPlay={() => navigate(`/play/${BUILTIN_STORY_ID}`)}
          />
          {published.map((story) => (
            <StoryCard
              key={story.id}
              storyLite={{ title: story.title, subtitle: story.subtitle }}
              plays={story.plays ?? 0}
              author={story.author?.display_name ?? "Anonymous Dev"}
              onPlay={() => navigate(`/play/${story.id}`)}
            />
          ))}
          {!loading && published.length === 0 ? (
            <p className="muted">No community stories yet — be the first!</p>
          ) : null}
        </div>
      </section>

      <section className="hub-section">
        <h2>Your stories</h2>
        <div className="story-grid">
          {mine.map((story) => (
            <StoryCard
              key={story.id}
              storyLite={{
                title: story.title,
                subtitle: story.subtitle,
              }}
              plays={story.plays ?? 0}
              author="you"
              badge={story.published ? "published" : "draft"}
              onPlay={() => navigate(`/play/${story.id}`)}
              onEdit={() => navigate(`/edit/story/${story.id}`)}
            />
          ))}
          {!loading && mine.length === 0 ? (
            <p className="muted">No stories yet. Create one!</p>
          ) : null}
        </div>
      </section>

      <section className="hub-section">
        <h2>Your characters</h2>
        <div className="story-grid">
          {characters.map((c) => (
            <CharacterCard key={c.id} character={c} />
          ))}
          {!loading && characters.length === 0 ? (
            <p className="muted">No characters yet. Build a cast!</p>
          ) : null}
        </div>
      </section>
    </div>
  );
}

function OfflineHub() {
  return (
    <div className="hub">
      <header className="hub-header">
        <h1 className="hub-logo">DevAdventure2K</h1>
      </header>
      <ToastBanner
        kind="warn"
        message="No Supabase configured — running offline. Only the built-in story is available. Copy .env.example → .env and set VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY to enable the community hub and editors."
      />
      <section className="hub-section">
        <h2>Play a story</h2>
        <div className="story-grid">
          <StoryCard
            storyLite={{
              title: BUILTIN_STORY_TITLE,
              subtitle: "The bundled dev-roguelike. Works offline.",
            }}
            plays={0}
            author="DevAdventure2K"
            onPlay={() => navigate(`/play/${BUILTIN_STORY_ID}`)}
          />
        </div>
      </section>
    </div>
  );
}

interface StoryCardProps {
  storyLite: { title: string; subtitle: string };
  plays: number;
  author: string;
  badge?: string;
  onPlay: () => void;
  onEdit?: () => void;
}

function StoryCard({
  storyLite,
  plays,
  author,
  badge,
  onPlay,
  onEdit,
}: StoryCardProps) {
  return (
    <div className="story-card">
      <div className="story-card-body">
        <h3 className="story-card-title">
          {storyLite.title}
          {badge ? (
            <span className={`badge badge-${badge}`}>{badge}</span>
          ) : null}
        </h3>
        {storyLite.subtitle ? (
          <p className="story-card-sub">{storyLite.subtitle}</p>
        ) : null}
        <p className="story-card-meta">
          by {author} · {plays} plays
        </p>
      </div>
      <ButtonGroup>
        <button className="choice-button" onClick={onPlay}>
          ▶ Play
        </button>
        {onEdit ? (
          <button className="choice-button" onClick={onEdit}>
            ✎ Edit
          </button>
        ) : null}
      </ButtonGroup>
    </div>
  );
}

function CharacterCard({ character }: { character: CharacterRow }) {
  return (
    <div className="story-card">
      <div className="story-card-body">
        <h3 className="story-card-title">
          <span className="avatar-emoji">{character.avatar_emoji ?? "🧑‍💻"}</span>
          {character.title}
          {character.public ? (
            <span className="badge badge-shared">shared</span>
          ) : null}
        </h3>
        {character.role ? (
          <p className="story-card-sub">{character.role}</p>
        ) : null}
        {character.traits?.length ? (
          <p className="story-card-meta">
            {character.traits.slice(0, 4).join(" · ")}
          </p>
        ) : null}
      </div>
      <ButtonGroup>
        <button
          className="choice-button"
          onClick={() => navigate(`/edit/character/${character.id}`)}
        >
          ✎ Edit
        </button>
      </ButtonGroup>
    </div>
  );
}
